// store/features/ai-events/aiEventsAPI.ts

import { baseApi } from '@/services/api';
import {
  AIGeneration,
  AITemplate,
  AISession,
  AIMessage,
  EventStructure,
  GenerateEventRequest,
  GenerateEventResponse,
  GenerateTemplateRequest,
  GenerateTemplateResponse,
  EnhanceDescriptionRequest,
  AnalyzeEventImageRequest,
  CreateAISessionRequest,
  SendAIMessageRequest,
  AIModelsResponse,
} from '@/store/features/ai-events/aiEventsTypes';

// Get user role from Redux
const user = localStorage.getItem('authUser') ? JSON.parse(localStorage.getItem('authUser')!) : null;
const userRole = user?.userType || null;
export const aiEventsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ==========================================
    // EVENT GENERATION
    // ==========================================

    generateEvent: builder.mutation<GenerateEventResponse, any>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/generate-event`,
        method: 'POST',
        data: {
          ...data,
          saveSections: true, // Flag to save sections
        },
      }),
      invalidatesTags: ['AIGenerations', 'Events'],
    }),

    generateTemplateStructure: builder.mutation<GenerateTemplateResponse, GenerateTemplateRequest>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/generate-template`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AITemplates'],
    }),

    enhanceEventDescription: builder.mutation<{
      data: any; enhanced_description: string
    }, EnhanceDescriptionRequest>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/enhance-event-description`,
        method: 'POST',
        data,
      }),
    }),

    analyzeEventImage: builder.mutation<{ analysis: string; suggestions: string[] }, AnalyzeEventImageRequest>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/analyze-event-image`,
        method: 'POST',
        data,
      }),
    }),

    // ==========================================
    // AI SESSIONS
    // ==========================================

    createAISession: builder.mutation<AISession, any>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/sessions`,
        method: 'POST',
        data: {
          ...data,
          model: data.model || 'gemini', // ✅ Ensure model is always sent
        },
      }),
      invalidatesTags: ['AISessions'],
    }),

    getAISessions: builder.query<{ data: AISession[] }, void>({
      query: () => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/sessions`,
        method: 'GET',
      }),
      providesTags: ['AISessions'],
    }),

    getSessionMessages: builder.query<{ data: any }, { sessionId: any; limit?: number }>({
      query: ({ sessionId, limit = 50 }) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/sessions/${sessionId}/messages?limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['AIMessages'],
    }),

    updateAISession: builder.mutation<AISession, { sessionId: string; data: Partial<AISession> }>({
      query: ({ sessionId, data }) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/sessions/${sessionId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AISessions'],
    }),

    deleteAISession: builder.mutation<{ success: boolean }, string>({
      query: (sessionId) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AISessions'],
    }),

    // ==========================================
    // AI CHAT/MESSAGING
    // ==========================================

    getAIModels: builder.query<AIModelsResponse, void>({
      query: () => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/ai/models`,
        method: 'GET',
      }),
    }),

    sendAIMessage: builder.mutation<AIMessage, any>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/ai/message`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AIMessages'],
    }),

    streamAIMessage: builder.mutation<ReadableStream, SendAIMessageRequest>({
      queryFn: async (data, _api, _extraOptions, baseQuery) => {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/custom/ai/event/agent/setup/call/secure/${userRole}/ai/stream/message`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              },
              body: JSON.stringify(data),
            }
          );

          if (!response.ok) {
            throw new Error('Stream failed');
          }

          return { data: response.body! };
        } catch (error: any) {
          return { error: { status: 500, data: error.message } };
        }
      },
    }),

    // ==========================================
    // AI GENERATIONS (CRUD)
    // ==========================================

    getAIGenerations: builder.query<{ data: AIGeneration[] }, { event_id?: string; type?: string }>({
      query: ({ event_id, type }) => {
        const params = new URLSearchParams();
        if (event_id) params.append('event_id', event_id);
        if (type) params.append('type', type);

        return {
          url: `/custom/${userRole}/ai-generation/generations?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['AIGenerations'],
    }),

    getAIGenerationById: builder.query<{ data: AIGeneration }, string>({
      query: (id) => ({
        url: `/custom/${userRole}/ai-generation/generations/${id}`,
        method: 'GET',
      }),
      providesTags: ['AIGenerations'],
    }),

    createAIGeneration: builder.mutation<{ data: AIGeneration }, Partial<AIGeneration>>({
      query: (data) => ({
        url: `/custom/${userRole}/ai-generation/generations`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AIGenerations'],
    }),

    updateAIGeneration: builder.mutation<{ data: AIGeneration }, { id: string; data: Partial<AIGeneration> }>({
      query: ({ id, data }) => ({
        url: `/custom/${userRole}/ai-generation/generations/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AIGenerations'],
    }),

    deleteAIGeneration: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/custom/${userRole}/ai-generation/generations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AIGenerations'],
    }),

    // ==========================================
    // AI TEMPLATES (CRUD)
    // ==========================================

    getAITemplates: builder.query<{ data: AITemplate[] }, { industry?: string; is_active?: boolean }>({
      query: ({ industry, is_active }) => {
        const params = new URLSearchParams();
        if (industry) params.append('industry', industry);
        if (is_active !== undefined) params.append('is_active', String(is_active));

        return {
          url: `/custom/${userRole}/ai-template/templates?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['AITemplates'],
    }),

    getAITemplateById: builder.query<{ data: AITemplate }, string>({
      query: (id) => ({
        url: `/custom/${userRole}/ai-template/templates/${id}`,
        method: 'GET',
      }),
      providesTags: ['AITemplates'],
    }),

    createAITemplate: builder.mutation<{ data: AITemplate }, Partial<AITemplate>>({
      query: (data) => ({
        url: `/custom/${userRole}/ai-template/templates`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AITemplates'],
    }),

    updateAITemplate: builder.mutation<{ data: AITemplate }, { id: string; data: Partial<AITemplate> }>({
      query: ({ id, data }) => ({
        url: `/custom/${userRole}/ai-template/templates/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AITemplates'],
    }),

    deleteAITemplate: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/custom/${userRole}/ai-template/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AITemplates'],
    }),
  }),
});

// Export hooks
export const {
  // Event Generation
  useGenerateEventMutation,
  useGenerateTemplateStructureMutation,
  useEnhanceEventDescriptionMutation,
  useAnalyzeEventImageMutation,

  // AI Sessions
  useCreateAISessionMutation,
  useGetAISessionsQuery,
  useGetSessionMessagesQuery,
  useLazyGetSessionMessagesQuery,
  useUpdateAISessionMutation,
  useDeleteAISessionMutation,

  // AI Chat
  useGetAIModelsQuery,
  useSendAIMessageMutation,
  useStreamAIMessageMutation,

  // AI Generations CRUD
  useGetAIGenerationsQuery,
  useLazyGetAIGenerationsQuery,
  useGetAIGenerationByIdQuery,
  useCreateAIGenerationMutation,
  useUpdateAIGenerationMutation,
  useDeleteAIGenerationMutation,

  // AI Templates CRUD
  useGetAITemplatesQuery,
  useLazyGetAITemplatesQuery,
  useGetAITemplateByIdQuery,
  useCreateAITemplateMutation,
  useUpdateAITemplateMutation,
  useDeleteAITemplateMutation,
} = aiEventsAPI;
